import React, { useState, useEffect } from "react";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";
import { useData } from "../contexts/DataContext";
import { motion, AnimatePresence } from "framer-motion";

const HeroSection = () => {
  const { heroSlides, loading } = useData();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  
  // Filter active slides and sort by order
  const slides = heroSlides
    .filter(slide => slide.active !== false) // Backend uses 'active' not 'is_active'
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  // Auto-advance slides (pause on hover)
  useEffect(() => {
    if (!isHovered) {
      const timer = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [slides.length, isHovered]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const currentSlideData = slides[currentSlide];

  // Show loading state
  if (loading) {
    return (
      <section className="relative h-screen overflow-hidden bg-stone-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#81be00] mx-auto mb-4"></div>
          <p className="text-white">Loading...</p>
        </div>
      </section>
    );
  }

  // Show default slide if no slides available
  if (!slides || slides.length === 0) {
    return (
      <section className="relative h-screen overflow-hidden bg-stone-900 flex items-center justify-center">
        <div className="text-white text-center z-10 px-4">
          <h1 className="text-5xl md:text-7xl font-thin tracking-[0.25em] mb-8 title-font">
            NYINIMU PROPERTIES
          </h1>
          <p className="text-lg font-light tracking-widest opacity-95 uppercase">
            DAILY PAYMENTS. YOUR HOME. BUY DAILY. OWN FOREVER.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      className="relative h-screen overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Carousel Container */}
      <div className="relative w-full h-full">
        {/* Background Image */}
        <div className="absolute inset-0 w-full h-full">
          <AnimatePresence mode="wait">
            <motion.img
              key={`bg-${currentSlide}`}
              src={currentSlideData.background_image}
              alt={`${currentSlideData.title} - Exterior`}
              className="w-full h-full object-cover"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1 }}
            />
          </AnimatePresence>
          <div className="absolute inset-0 bg-black bg-opacity-30"></div>
        </div>

        {/* Interior Image - Right side with angled cut */}
        <div className="absolute top-0 right-0 w-full lg:w-5/12 h-full">
          <div
            className="w-full h-full relative"
            style={{
              clipPath: "polygon(20% 0%, 100% 0%, 100% 100%, 0% 100%)",
            }}
          >
            <AnimatePresence mode="wait">
              <motion.img
                key={`interior-${currentSlide}`}
                src={currentSlideData.interior_image}
                alt={`${currentSlideData.title} - Interior`}
                className="w-full h-full object-cover"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 1 }}
              />
            </AnimatePresence>
            <div className="absolute top-0 left-0 w-1 h-full bg-orange-400 opacity-80"></div>
          </div>
        </div>

        {/* Hero Content */}
        <div className="absolute inset-0 flex items-center">
          <div className="text-white z-10 ml-4 md:ml-8 lg:ml-12 xl:ml-16 max-w-5xl">
            <AnimatePresence mode="wait">
              <motion.div
                key={`text-${currentSlide}`}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.8 }}
              >
                <h1 className="text-3xl md:text-5xl lg:text-6xl xl:text-7xl 2xl:text-8xl font-thin tracking-[0.25em] mb-8 leading-none text-shadow-lg title-font">
                  {currentSlideData.title}
                </h1>
                <p className="text-sm md:text-base lg:text-lg font-light tracking-widest opacity-95 max-w-md uppercase">
                  {currentSlideData.subtitle}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Carousel Navigation Arrows */}
        <div className="absolute left-8 top-1/2 transform -translate-y-1/2 z-20">
          <div className="flex flex-col space-y-4">
            <Button
              aria-label="Previous Slide"
              variant="ghost"
              size="sm"
              onClick={prevSlide}
              className="w-12 h-12 rounded-full bg-black bg-opacity-50 hover:bg-black hover:bg-opacity-70 text-white border border-white border-opacity-30 hover:border-opacity-70 transition-all duration-300"
            >
              <ChevronLeft size={20} />
            </Button>

            <Button
              aria-label="Next Slide"
              variant="ghost"
              size="sm"
              onClick={nextSlide}
              className="w-12 h-12 rounded-full bg-black bg-opacity-50 hover:bg-black hover:bg-opacity-70 text-white border border-white border-opacity-30 hover:border-opacity-70 transition-all duration-300"
            >
              <ChevronRight size={20} />
            </Button>
          </div>
        </div>

        {/* Slide Indicators */}
        <div className="absolute bottom-32 left-8 z-20">
          <div className="flex flex-col space-y-3">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
                className={`w-1 h-12 transition-all duration-300 ${
                  index === currentSlide
                    ? "bg-white"
                    : "bg-white bg-opacity-40 hover:bg-opacity-70"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Scroll Down Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 lg:left-1/3">
          <div className="flex flex-col items-center text-white">
            <div className="w-10 h-10 border border-white flex items-center justify-center mb-2 hover:bg-white hover:text-black transition-all duration-300 cursor-pointer">
              <ChevronDown size={20} />
            </div>
          </div>
        </div>

        {/* Slide Progress Bar */}
        <div className="absolute bottom-0 left-0 right-0 z-20">
          <div className="w-full h-1 bg-black bg-opacity-30">
            <motion.div
              key={`progress-${currentSlide}-${isHovered}`} // reset on hover
              className="h-full bg-white"
              initial={{ width: "0%" }}
              animate={{ width: isHovered ? "0%" : "100%" }}
              transition={{ duration: isHovered ? 0 : 5, ease: "linear" }}
            />
          </div>
        </div>

        {/* Decorative Slide Counter */}
        <div className="absolute bottom-16 right-16 hidden lg:block z-10">
          <div className="text-white text-right">
            <div className="text-sm opacity-70 mb-1">
              {String(currentSlide + 1).padStart(2, "0")} /{" "}
              {String(slides.length).padStart(2, "0")}
            </div>
            <div className="w-px h-12 bg-white opacity-60 ml-auto"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
